import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { initialMaterialsData, mockStockIns, mockSettlements, approvalTasks, mockOrders, mockDeliveries, demandData, baseData, mockEmployees, mockReservations, initialSupplierData, mockInvoices, initialNotifications, mockOperationLogs } from './data/mockData';
import { ORDER_STATUS, DELIVERY_STATUS } from './constants/status';

// Components
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import ToastContainer from './components/ToastContainer';

// Pages
import LoginView from './pages/LoginView';
import MobileApp from './pages/MobileApp';
import DashboardView from './pages/DashboardView';
import ApprovalView from './pages/ApprovalView';
import BaseView from './pages/BaseView';
import MaterialsView from './pages/MaterialsView';
import DemandView from './pages/DemandView';
import OrderView from './pages/OrderView';
import OrderDetailView from './pages/OrderDetailView';
import DeliveryView from './pages/DeliveryView';
import DeliveryDetailView from './pages/DeliveryDetailView';
import ArrivalNoticeView from './pages/ArrivalNoticeView';
import StockinManageView from './pages/StockinManageView';
import SettlementManageView from './pages/SettlementManageView';
import InvoiceManageView from './pages/InvoiceManageView';
import DocumentView from './pages/DocumentView';
import DocumentDetailView from './pages/DocumentDetailView';
import ReservationsView from './pages/ReservationsView';
import PaymentView from './pages/PaymentView';
import PaymentDetailView from './pages/PaymentDetailView';
import UsersView from './pages/UsersView';
import SettingsView from './pages/SettingsView';
import UserCenterView from './pages/UserCenterView';
import RolePermissionView from './pages/RolePermissionView';
import SubAccountView from './pages/SubAccountView';
import OperationLogView from './pages/OperationLogView';

// 单据类型标签
const DOC_TYPE_LABELS = {
  stockin: '入库单',
  settlement: '结算单',
  invoice: '发票单',
  payment: '付款单',
};

export default function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [currentRole, setCurrentRole] = useState('admin');
  const [isMobile, setIsMobile] = useState(false);
  const [forceMobileView, setForceMobileView] = useState(false);

  // 筛选条件状态（从首页跳转时传递）
  const [initialFilter, setInitialFilter] = useState(null);

  // Detail view states
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [documentSource, setDocumentSource] = useState(null); // 'list' | 'deliveryDetail' | 'paymentDetail'

  // Data State
  const [materials, setMaterials] = useState(initialMaterialsData);
  const [stockIns] = useState(mockStockIns);
  const [settlements] = useState(mockSettlements);
  const [approvals, setApprovals] = useState(approvalTasks);
  const [orders] = useState(mockOrders);
  const [deliveries, setDeliveries] = useState(mockDeliveries);
  const [demands, setDemands] = useState(demandData);
  const [bases, setBases] = useState(baseData);
  const [employees] = useState(mockEmployees);
  const [reservations, setReservations] = useState(mockReservations);
  const [users, setUsers] = useState(initialSupplierData);
  const [invoices] = useState(mockInvoices);

  // Notifications & Toast State
  const [notifications, setNotifications] = useState(initialNotifications);
  const [toasts, setToasts] = useState([]);

  // 切换菜单时清除详情状态
  const handleTabChange = useCallback((tab, filter = null) => {
    setActiveTab(tab);
    setInitialFilter(filter);
    setSelectedOrder(null);
    setSelectedDelivery(null);
    setSelectedDocument(null);
    setSelectedPayment(null);
  }, []);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Todo count calculation
  const todoCount = useMemo(() => {
    const pendingApprovals = approvals.filter(a => a.status === 'pending').length;
    const pendingOrders = orders.filter(o => o.status === ORDER_STATUS.PENDING_APPROVAL).length;
    return pendingApprovals + pendingOrders;
  }, [approvals, orders]);

  // Delivery waiting approval count
  const deliveryWaitingCount = useMemo(() => {
    return deliveries.filter(d => d.status === DELIVERY_STATUS.WAITING_APPROVAL).length;
  }, [deliveries]);

  // Toast methods
  const showToast = useCallback((type, message) => {
    const id = `toast-${Date.now()}`;
    setToasts(prev => [...prev, { id, type, message }]);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  // Notification methods
  const markAsRead = useCallback((id) => {
    setNotifications(prev =>
      prev.map(n => n.id === id ? { ...n, read: true } : n)
    );
  }, []);

  const markAllAsRead = useCallback(() => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  }, []);

  const handleLogin = (oaId) => {
    // Find employee by ID or create a default user
    const user = employees.find(e => e.id === oaId) || { 
      id: oaId, 
      name: oaId, 
      role: '管理员', 
      base: '平台运营中心', 
      phone: '138****8888', 
      status: '在职',
      email: `${oaId}@example.com`
    };
    setIsLoggedIn(true);
    setCurrentUser(user);
    setCurrentRole('admin');
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setCurrentUser(null);
  };


  // 付款审批操作
  const handlePaymentApprove = (payment) => {
    // 更新对应送货单的付款状态：审批中 -> 待付款
    setDeliveries(deliveries.map(d => {
      if (d.payment && d.payment.no === payment.docNo) {
        return {
          ...d,
          payment: {
            ...d.payment,
            pendingApproval: false,
            pendingPay: true,
          }
        };
      }
      return d;
    }));
    showToast('success', `付款单 ${payment.docNo} 审批通过`);
  };

  // 付款操作
  const handlePaymentPay = (payment) => {
    const now = new Date().toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).replace(/\//g, '-');
    const transactionNo = `TXN${Date.now()}`;

    // 更新对应送货单的付款状态：待付款 -> 已付款
    setDeliveries(deliveries.map(d => {
      if (d.payment && d.payment.no === payment.docNo) {
        return {
          ...d,
          payment: {
            ...d.payment,
            pendingPay: false,
            at: now,
            transactionNo: transactionNo,
            paidAmount: payment.requiredAmount,
          },
          status: DELIVERY_STATUS.PAID,
        };
      }
      return d;
    }));
    showToast('success', `付款单 ${payment.docNo} 付款成功`);
  };


  const handleApproveTask = (taskId) => {
    setApprovals(approvals.map(t => t.id === taskId ? { ...t, status: 'approved', currentStep: '已完成' } : t));
    showToast('success', `审批任务 ${taskId} 已通过`);
  };

  const handleCreateDemand = (newDemand) => {
    setDemands([newDemand, ...demands]);
    showToast('success', `采购需求已发布: ${newDemand.material}`);
  };

  const handleUpdateDemand = (demandId, updates) => {
    setDemands(demands.map(d => d.id === demandId ? { ...d, ...updates } : d));
    if (updates.status === '已失效') {
      showToast('warning', `需求 ${demandId} 已关闭`);
    } else if (updates.status === '草稿') {
      showToast('success', `需求 ${demandId} 已撤回`);
    } else {
      showToast('success', `需求 ${demandId} 已更新`);
    }
  };

  const handleAddMaterial = (newMaterial) => {
    setMaterials([...materials, newMaterial]);
    showToast('success', `新物料已添加: ${newMaterial.name}`);
  };

  const handleCreateReservation = (newReservation) => {
    setReservations([newReservation, ...reservations]);
    showToast('success', `预约已创建: ${newReservation.id}`);
  };

  const handleAddBase = (newBase) => {
    setBases([...bases, newBase]);
    showToast('success', `新基地已添加: ${newBase.name}`);
  };

  const handleUpdateBase = (updatedBase) => {
    setBases(bases.map(b => b.id === updatedBase.id ? updatedBase : b));
    showToast('success', `基地信息已更新: ${updatedBase.name}`);
  };

  const handleStopBase = (baseId) => {
    setBases(bases.map(b => b.id === baseId ? { ...b, platformStatus: '已停运' } : b));
    showToast('warning', '基地已停运');
  };

  const handleRestartBase = (baseId) => {
    setBases(bases.map(b => b.id === baseId ? { ...b, platformStatus: '运营中' } : b));
    showToast('success', '基地已开启');
  };

  const handleUpdateMaterial = (updatedMaterial) => {
    setMaterials(materials.map(m => m.id === updatedMaterial.id ? updatedMaterial : m));
    showToast('success', `物料信息已更新: ${updatedMaterial.name}`);
  };

  const handleDeleteMaterial = (materialId) => {
    setMaterials(materials.filter(m => m.id !== materialId));
    showToast('warning', '物料已删除');
  };

  const handleUpdateUser = (updatedUser) => {
    setUsers(users.map(u => u.id === updatedUser.id ? updatedUser : u));
    showToast('success', `用户信息已更新: ${updatedUser.name}`);
  };

  if (!isLoggedIn) {
    return <LoginView onLogin={handleLogin} />;
  }

  if (isMobile || forceMobileView) {
    return (
      <MobileApp 
        currentRole={currentRole} 
        onRoleChange={setCurrentRole} 
        forceMobilePreview={forceMobileView}
        onExitMobilePreview={() => setForceMobileView(false)}
        orders={orders}
        stockIns={stockIns}
        reservations={reservations}
        demands={demands}
        approvals={approvals}
      />
    );
  }

  const renderContent = () => {
    // 单据详情页 - 最优先检查
    if (selectedDocument) {
      const order = orders.find(o => o.id === selectedDocument.orderId);
      return (
        <DocumentDetailView
          document={selectedDocument}
          order={order}
          deliveries={deliveries}
          stockIns={stockIns}
          onBack={() => {
            setSelectedDocument(null);
            // 根据来源决定是否恢复送货单详情
            if (documentSource === 'deliveryDetail' && selectedDelivery) {
              // 从送货单详情进入，不清除 selectedDelivery，会自动显示送货单详情
            }
            setDocumentSource(null);
          }}
          onviewDelivery={(deliveryId) => {
            const delivery = deliveries.find(d => d.id === deliveryId);
            if (delivery) {
              setSelectedDocument(null);
              setDocumentSource(null);
              setSelectedDelivery(delivery);
            }
          }}
          onNavigateToDocument={(doc) => {
            // 直接设置选中的单据，实现单据间跳转
            // 保持当前来源不变
            setSelectedDocument(doc);
          }}
        />
      );
    }

    // 付款单详情页
    if (selectedPayment) {
      return (
        <PaymentDetailView
          payment={selectedPayment}
          onBack={() => setSelectedPayment(null)}
          onInvoiceClick={(invoiceNo) => {
            // 查找所有关联此发票的送货单
            const relatedDeliveries = deliveries.filter(d => d.invoice?.no === invoiceNo);
            if (relatedDeliveries.length > 0) {
              const delivery = relatedDeliveries[0];
              // 收集所有关联的结算单号（去重）
              const settlementNos = [...new Set(relatedDeliveries.map(d => d.settlement?.settlementNo).filter(Boolean))];

              setSelectedPayment(null);
              setSelectedDocument({
                ...delivery,
                docType: 'invoice',
                docNo: invoiceNo,
                invoiceNo: invoiceNo,
                invoiceType: delivery.invoice?.type,
                amount: delivery.invoice?.amount,
                invoiceDate: delivery.invoice?.at,
                invoiceFile: delivery.invoice?.file,
                status: delivery.payment ? '已付款' : (delivery.invoice?.pendingApproval ? '审批中' : '已开票'),
                createdAt: delivery.invoice?.at,
                delivery,
                // 添加合并单据相关字段
                isMerged: settlementNos.length >= 2,
                settlementNos: settlementNos.length > 0 ? settlementNos : null,
                sourceSettlementNos: settlementNos.length > 0 ? settlementNos : null,
              });
            }
          }}
          onSettlementClick={(settlementNo) => {
            const delivery = deliveries.find(d => d.settlement?.settlementNo === settlementNo);
            if (delivery) {
              setSelectedPayment(null);
              setSelectedDocument({
                ...delivery,
                docType: 'settlement',
                docNo: settlementNo,
                settlementNo: settlementNo,
                supplierName: delivery.supplierName,
                baseName: delivery.baseName,
                materialName: delivery.materialName,
                unitPrice: delivery.settlement?.unitPrice,
                actualWeight: delivery.settlement?.actualWeight,
                amount: delivery.settlement?.amount,
                status: delivery.settlement?.confirmedBySupplier ? '已确认' : (delivery.settlement?.approved ? '待确认' : '待审核'),
                createdAt: delivery.settlement?.at,
                stockInNos: delivery.settlement?.sourceStockInNos || (delivery.storage?.stockInNo ? [delivery.storage.stockInNo] : []),
                delivery,
              });
            }
          }}
        />
      );
    }

    // 送货单详情页 - 优先检查，这样从订单详情点击送货单可以正确跳转
    if (selectedDelivery) {
      const order = orders.find(o => o.id === selectedDelivery.orderId);
      return (
        <DeliveryDetailView
          delivery={selectedDelivery}
          order={order}
          onBack={() => setSelectedDelivery(null)}
          onConfirmReservation={(delivery) => {
            setDeliveries(deliveries.map(d => d.id === delivery.id ? { ...d, status: DELIVERY_STATUS.WAITING_DELIVERY } : d));
            showToast('success', `送货单 ${delivery.deliveryNo} 预约已确认`);
            setSelectedDelivery(null);
          }}
          onConfirmArrival={(delivery) => {
            const order = orders.find(o => o.id === delivery.orderId);
            const newStatus = order?.status === ORDER_STATUS.IN_PROGRESS ? DELIVERY_STATUS.ARRIVED : DELIVERY_STATUS.WAITING_APPROVAL;
            setDeliveries(deliveries.map(d => d.id === delivery.id ? { ...d, status: newStatus } : d));
            showToast('success', `送货单 ${delivery.deliveryNo} 已确认到场`);
            setSelectedDelivery(null);
          }}
          onStore={(delivery) => {
            // 模拟入库
            const weight = 10 + Math.random() * 20;
            setDeliveries(deliveries.map(d => d.id === delivery.id ? {
              ...d,
              status: DELIVERY_STATUS.STORED,
              storage: {
                at: new Date().toLocaleString(),
                stockInNo: `SI${Date.now()}`,
                grossWeight: 35.5,
                tareWeight: 15.2,
                netWeight: 20.3,
                deduction: 0.5,
                actualWeight: weight,
                quality: '一级',
                photos: [],
                operator: currentUser?.name || '系统',
              }
            } : d));
            showToast('success', `送货单 ${delivery.deliveryNo} 已入库，实际重量 ${weight.toFixed(1)} 吨`);
            setSelectedDelivery(null);
          }}
          onViewArrivalNotice={(delivery) => {
            // 跳转到预到货通知单详情（从送货单详情进入）
            setDocumentSource('deliveryDetail');
            setSelectedDocument({
              ...delivery,
              docType: 'arrival_notice',
              docNo: delivery.reservation?.reservationNo,
              reservationTime: delivery.reservation?.time,
              vehicleNo: delivery.reservation?.vehicleNo,
              driverName: delivery.reservation?.driverName,
              driverPhone: delivery.reservation?.driverPhone,
              status: '已确认',
              delivery,
            });
          }}
          onViewStockin={(delivery) => {
            // 跳转到入库单详情（从送货单详情进入）
            // 优先使用 storage 中的金额信息，其次使用 settlement 中的单价计算
            const price = delivery.storage?.price || delivery.settlement?.unitPrice || delivery.price;
            const taxRate = delivery.storage?.taxRate || 0.13;
            const amountWithTax = delivery.storage?.amountWithTax || (delivery.storage?.actualWeight * price);
            const amountWithoutTax = delivery.storage?.amountWithoutTax || (amountWithTax / (1 + taxRate));

            setDocumentSource('deliveryDetail');
            setSelectedDocument({
              ...delivery,
              docType: 'stockin',
              docNo: delivery.storage?.stockInNo,
              deliveryId: delivery.id,
              stockInTime: delivery.storage?.at,
              vehicleNo: delivery.reservation?.vehicleNo,
              operator: delivery.storage?.operator,
              grossWeight: delivery.storage?.grossWeight,
              tareWeight: delivery.storage?.tareWeight,
              actualWeight: delivery.storage?.actualWeight,
              deduction: delivery.storage?.deduction,
              amountWithTax: amountWithTax,
              amountWithoutTax: amountWithoutTax,
              price: price,
              taxRate: taxRate * 100,
              invoiceType: delivery.storage?.invoiceType,
              quality: delivery.storage?.quality,
              photos: delivery.storage?.photos,
              status: delivery.settlement ? '已结算' : '已入库',
              // 环节信息（包含照片）
              entryInfo: delivery.storage?.entryInfo,
              entryWeighInfo: delivery.storage?.entryWeighInfo,
              qualityInfo: delivery.storage?.qualityInfo,
              exitWeighInfo: delivery.storage?.exitWeighInfo,
              exitCheckInfo: delivery.storage?.exitCheckInfo,
              delivery,
            });
          }}
          onViewSettlement={(delivery) => {
            // 跳转到结算单详情（从送货单详情进入）
            setDocumentSource('deliveryDetail');
            setSelectedDocument({
              ...delivery,
              docType: 'settlement',
              docNo: delivery.settlement?.settlementNo,
              settlementNo: delivery.settlement?.settlementNo,
              supplierName: delivery.supplierName,
              baseName: delivery.baseName,
              materialName: delivery.materialName,
              unitPrice: delivery.settlement?.unitPrice,
              actualWeight: delivery.settlement?.actualWeight || delivery.storage?.actualWeight,
              amount: delivery.settlement?.amount,
              confirmedBySupplier: delivery.settlement?.confirmedBySupplier,
              confirmedAt: delivery.settlement?.confirmedAt,
              delivery,
            });
          }}
          onViewInvoice={(delivery) => {
            // 跳转到发票单详情（从送货单详情进入）
            setDocumentSource('deliveryDetail');
            setSelectedDocument({
              ...delivery,
              docType: 'invoice',
              docNo: delivery.invoice?.no,
              invoiceNo: delivery.invoice?.no,
              invoiceType: delivery.invoice?.type,
              amount: delivery.invoice?.amount,
              invoiceDate: delivery.invoice?.at,
              invoiceFile: delivery.invoice?.file,
              status: delivery.payment ? '已付款' : (delivery.invoice?.pendingApproval ? '审批中' : '已开票'),
              createdAt: delivery.invoice?.at,
              delivery,
            });
          }}
          onViewPayment={(delivery) => {
            // 跳转到付款单详情（从送货单详情进入）
            setDocumentSource('deliveryDetail');
            // 构建完整的 payment 对象，包含 delivery 引用和合并单据属性
            const payment = delivery.payment;
            let paymentStatus = '已付款';
            if (payment.pendingApproval) {
              paymentStatus = '待审批';
            } else if (payment.pendingPay) {
              paymentStatus = '待付款';
            }
            setSelectedPayment({
              ...payment,
              docNo: payment.no,
              status: paymentStatus,
              delivery, // 包含 delivery 引用以访问 invoice 和 settlement
              isMerged: (payment.sourceInvoiceNos?.length || 0) >= 2,
              sourceInvoiceNos: payment.sourceInvoiceNos || null,
              sourceSettlementNos: payment.sourceSettlementNos || null,
              sourceInvoiceAmounts: payment.sourceInvoiceAmounts || null,
              sourceInvoiceTypes: payment.sourceInvoiceTypes || null,
              sourceInvoiceDates: payment.sourceInvoiceDates || null,
              sourceSettlementAmounts: payment.sourceSettlementAmounts || null,
              sourceSettlementWeights: payment.sourceSettlementWeights || null,
              sourceSettlementPrices: payment.sourceSettlementPrices || null,
            });
          }}
        />
      );
    }

    // 订单详情页
    if (selectedOrder) {
      const orderDeliveries = deliveries.filter(d => d.orderId === selectedOrder.id);
      return (
        <OrderDetailView
          order={selectedOrder}
          deliveries={orderDeliveries}
          onBack={() => setSelectedOrder(null)}
          onDeliveryClick={(delivery) => {
            setSelectedDelivery(delivery);
          }}
        />
      );
    }

    switch (activeTab) {
      case 'dashboard': return <DashboardView
        orders={orders}
        stockIns={stockIns}
        approvals={approvals}
        users={users}
        bases={bases}
        materials={materials}
        demands={demands}
        deliveries={deliveries}
        settlements={settlements}
        invoices={invoices}
        setActiveTab={handleTabChange}
      />;
      case 'approval': return <ApprovalView approvals={approvals} onApproveTask={handleApproveTask} initialFilter={initialFilter} setInitialFilter={setInitialFilter} />;
      case 'base': return <BaseView bases={bases} onAddBase={handleAddBase} onUpdateBase={handleUpdateBase} onStopBase={handleStopBase} onRestartBase={handleRestartBase} />;
      case 'materials': return <MaterialsView materials={materials} onAddMaterial={handleAddMaterial} onUpdateMaterial={handleUpdateMaterial} onDeleteMaterial={handleDeleteMaterial} />;
      case 'sub_account': return <SubAccountView />;
      case 'demand': return <DemandView demands={demands} materials={materials} onCreateDemand={handleCreateDemand} onUpdateDemand={handleUpdateDemand} initialFilter={initialFilter} setInitialFilter={setInitialFilter} />;
      case 'order': return <OrderView
        orders={orders}
        onOrderClick={(order) => setSelectedOrder(order)}
        initialFilter={initialFilter}
        setInitialFilter={setInitialFilter}
      />;
      case 'delivery': return <DeliveryView
        deliveries={deliveries}
        orders={orders}
        onDeliveryClick={(delivery) => setSelectedDelivery(delivery)}
        onOrderClick={(order) => setSelectedOrder(order)}
        initialFilter={initialFilter}
        setInitialFilter={setInitialFilter}
      />;
      case 'arrival_notice': return <ArrivalNoticeView
        deliveries={deliveries}
        orders={orders}
        onDetailClick={(doc) => {
          // 点击详情时，跳转到预到货通知单详情页（从列表进入）
          setDocumentSource('list');
          setSelectedDocument(doc);
        }}
        onOrderClick={(orderId) => {
          const order = orders.find(o => o.id === orderId);
          if (order) setSelectedOrder(order);
        }}
        onDeliveryClick={(deliveryId) => {
          const delivery = deliveries.find(d => d.id === deliveryId);
          if (delivery) setSelectedDelivery(delivery);
        }}
        initialFilter={initialFilter}
        setInitialFilter={setInitialFilter}
      />;
      case 'document': return <DocumentView
        deliveries={deliveries}
        orders={orders}
        onDocumentClick={(document) => setSelectedDocument(document)}
        onGenerateDocument={(generateInfo) => {
          const { targetType, targetLabel, sourceDocs, totalAmount, suppliers } = generateInfo;
          const newDocNo = `${targetType.toUpperCase()}${Date.now()}`;
          const supplierText = suppliers.length > 1 ? `(${suppliers.length}个供应商)` : `(${suppliers[0]})`;
          const label = targetLabel || DOC_TYPE_LABELS[targetType];
          showToast('success', `已生成${sourceDocs.length}条单据的合并${label}: ${newDocNo} ${supplierText}，总金额 ¥${totalAmount.toLocaleString()}`);
          // 实际项目中这里应该调用API创建新单据并更新状态
        }}
        onOrderClick={(orderId) => {
          const order = orders.find(o => o.id === orderId);
          if (order) setSelectedOrder(order);
        }}
        onDeliveryClick={(deliveryId) => {
          const delivery = deliveries.find(d => d.id === deliveryId);
          if (delivery) setSelectedDelivery(delivery);
        }}
      />;
      case 'reservation': return <ReservationsView reservations={reservations} orders={orders} onCreateReservation={handleCreateReservation} />;
      case 'stockin_manage': return <StockinManageView
        deliveries={deliveries}
        orders={orders}
        onDetailClick={(doc) => {
          setDocumentSource('list');
          setSelectedDocument(doc);
        }}
        onOrderClick={(orderId) => {
          const order = orders.find(o => o.id === orderId);
          if (order) setSelectedOrder(order);
        }}
        onDeliveryClick={(deliveryId) => {
          const delivery = deliveries.find(d => d.id === deliveryId);
          if (delivery) setSelectedDelivery(delivery);
        }}
        onGenerateDocument={(generateInfo) => {
          const { sourceDocs, totalAmount, suppliers } = generateInfo;
          const newDocNo = `SET${Date.now()}`;
          const supplierText = suppliers.length > 1 ? `(${suppliers.length}个供应商)` : `(${suppliers[0]})`;
          showToast('success', `已生成${sourceDocs.length}条入库单的合并结算单: ${newDocNo} ${supplierText}，总金额 ¥${totalAmount.toLocaleString()}`);
        }}
        initialFilter={initialFilter}
        setInitialFilter={setInitialFilter}
      />;
      case 'settlement_manage': return <SettlementManageView
        deliveries={deliveries}
        orders={orders}
        onDetailClick={(doc) => {
          setDocumentSource('list');
          setSelectedDocument(doc);
        }}
        onGenerateDocument={(generateInfo) => {
          const { sourceDocs, totalAmount, suppliers } = generateInfo;
          const newDocNo = `INV${Date.now()}`;
          const supplierText = suppliers.length > 1 ? `(${suppliers.length}个供应商)` : `(${suppliers[0]})`;
          showToast('success', `已生成${sourceDocs.length}条结算单的合并发票单: ${newDocNo} ${supplierText}，总金额 ¥${totalAmount.toLocaleString()}`);
        }}
        initialFilter={initialFilter}
        setInitialFilter={setInitialFilter}
        onStockInClick={(stockInNo) => {
          // 先在 deliveries 中查找对应的 delivery
          const delivery = deliveries.find(d => d.storage?.stockInNo === stockInNo);
          if (delivery) {
            // 从 delivery 构造入库单文档对象
            // 优先使用 storage 中的金额信息，其次使用 settlement 中的单价计算
            const price = delivery.storage?.price || delivery.settlement?.unitPrice || delivery.price;
            const taxRate = delivery.storage?.taxRate || 0.13;
            const amountWithTax = delivery.storage?.amountWithTax || (delivery.storage?.actualWeight * price);
            const amountWithoutTax = delivery.storage?.amountWithoutTax || (amountWithTax / (1 + taxRate));

            setSelectedDocument({
              ...delivery,
              docType: 'stockin',
              docNo: stockInNo,
              deliveryId: delivery.id,  // 明确设置 deliveryId，用于跳转到完整送货单
              stockInTime: delivery.storage?.at,
              vehicleNo: delivery.reservation?.vehicleNo,
              operator: delivery.storage?.operator,
              grossWeight: delivery.storage?.grossWeight,
              tareWeight: delivery.storage?.tareWeight,
              actualWeight: delivery.storage?.actualWeight,
              deduction: delivery.storage?.deduction,
              amountWithTax: amountWithTax,
              amountWithoutTax: amountWithoutTax,
              price: price,
              taxRate: taxRate * 100,
              invoiceType: delivery.storage?.invoiceType,
              status: delivery.settlement ? '已结算' : '已入库',
              // 环节信息（包含照片）
              entryInfo: delivery.storage?.entryInfo,
              entryWeighInfo: delivery.storage?.entryWeighInfo,
              qualityInfo: delivery.storage?.qualityInfo,
              exitWeighInfo: delivery.storage?.exitWeighInfo,
              exitCheckInfo: delivery.storage?.exitCheckInfo,
              photos: delivery.storage?.photos,
              delivery: delivery,
            });
          } else {
            // 如果在 deliveries 中找不到，在 stockIns 中查找
            const stockIn = stockIns.find(s => s.id === stockInNo);
            if (stockIn) {
              // 为 stockIns 数据构建模拟的环节信息（使用入库时间作为基准）
              const baseTime = stockIn.inboundTime || new Date().toISOString().slice(0, 16).replace('T', ' ');
              setSelectedDocument({
                docType: 'stockin',
                docNo: stockInNo,
                stockInTime: stockIn.inboundTime,
                vehicleNo: stockIn.plate,
                operator: stockIn.operator || '系统',
                grossWeight: stockIn.grossWeight,
                tareWeight: stockIn.tareWeight,
                actualWeight: stockIn.actualWeight,
                deduction: stockIn.deduction,
                amountWithTax: stockIn.totalAmount,
                amountWithoutTax: stockIn.totalAmount / (1 + 0.13),
                price: stockIn.price,
                taxRate: 13,
                status: stockIn.status,
                materialName: stockIn.materialName,
                supplierName: stockIn.supplier,
                baseName: stockIn.baseName,
                createdAt: stockIn.inboundTime,
                // 环节信息（使用占位符照片）
                entryInfo: {
                  at: baseTime,
                  operator: '门卫',
                  photo: 'https://picsum.photos/seed/entry_' + stockInNo + '/200/150',
                },
                entryWeighInfo: {
                  at: baseTime,
                  operator: stockIn.operator || '司磅员',
                  photo: 'https://picsum.photos/seed/weigh_in_' + stockInNo + '/200/150',
                },
                qualityInfo: {
                  at: baseTime,
                  operator: '质检员',
                  photo: 'https://picsum.photos/seed/quality_' + stockInNo + '/200/150',
                },
                exitWeighInfo: {
                  at: baseTime,
                  operator: stockIn.operator || '司磅员',
                  photo: 'https://picsum.photos/seed/weigh_out_' + stockInNo + '/200/150',
                },
                exitCheckInfo: {
                  at: baseTime,
                  operator: '门卫',
                  photo: 'https://picsum.photos/seed/exit_' + stockInNo + '/200/150',
                },
                photos: stockIn.photos || [],
                delivery: null,
              });
            }
          }
        }}
      />;
      case 'invoice_manage': return <InvoiceManageView
        deliveries={deliveries}
        orders={orders}
        onDetailClick={(doc) => {
          setDocumentSource('list');
          setSelectedDocument(doc);
        }}
        onSettlementClick={(settlementNo) => {
          // 根据结算单号找到所有相关的 deliveries（聚合逻辑与 SettlementManageView 一致）
          const relatedDeliveries = deliveries.filter(d => d.settlement?.settlementNo === settlementNo);

          if (relatedDeliveries.length > 0) {
            // 聚合数据
            const stockInNos = [];
            let totalActualWeight = 0;
            let totalAmount = 0;
            let createdAt = null;

            relatedDeliveries.forEach(d => {
              const stockInNo = d.storage?.stockInNo;
              if (stockInNo && !stockInNos.includes(stockInNo)) {
                stockInNos.push(stockInNo);
              }
              totalActualWeight += d.storage?.actualWeight || 0;
              totalAmount += d.settlement?.amount || 0;

              const dCreatedAt = d.storage?.at || d.createdAt;
              if (dCreatedAt && (!createdAt || dCreatedAt < createdAt)) {
                createdAt = dCreatedAt;
              }
            });

            const firstDelivery = relatedDeliveries[0];
            const isMerged = stockInNos.length >= 2;

            // 结算状态
            const isApproved = firstDelivery.settlement.approved !== false;
            const isConfirmed = firstDelivery.settlement.confirmedBySupplier === true;
            let settlementStatus;
            if (!isApproved) {
              settlementStatus = '待审核';
            } else if (isConfirmed) {
              settlementStatus = '已确认';
            } else {
              settlementStatus = '待确认';
            }

            setDocumentSource('list');
            // 构造完整的结算单文档对象
            setSelectedDocument({
              id: settlementNo,
              docType: 'settlement',
              docNo: settlementNo,
              // 基本信息
              supplierId: firstDelivery.supplierId,
              supplierName: firstDelivery.supplierName,
              baseId: firstDelivery.baseId,
              baseName: firstDelivery.baseName,
              materialId: firstDelivery.materialId,
              materialName: firstDelivery.materialName,
              // 聚合字段
              stockInNos: stockInNos,
              isMerged: isMerged,
              actualWeight: Math.round(totalActualWeight * 100) / 100,
              amount: Math.round(totalAmount * 100) / 100,
              unitPrice: firstDelivery.settlement.unitPrice,
              // 状态
              status: settlementStatus,
              confirmedBySupplier: isConfirmed,
              confirmedAt: firstDelivery.settlement.confirmedAt,
              createdAt: createdAt,
              // 关联的原始送货单
              delivery: firstDelivery,
              deliveries: relatedDeliveries,
            });
          }
        }}
        onGenerateDocument={(generateInfo) => {
          const { sourceDocs, totalAmount, suppliers } = generateInfo;
          const newDocNo = `PAY${Date.now()}`;
          const supplierText = suppliers.length > 1 ? `(${suppliers.length}个供应商)` : `(${suppliers[0]})`;
          showToast('success', `已生成${sourceDocs.length}条发票单的合并付款单: ${newDocNo} ${supplierText}，总金额 ¥${totalAmount.toLocaleString()}`);
        }}
        initialFilter={initialFilter}
        setInitialFilter={setInitialFilter}
      />;
      case 'payment': return <PaymentView
        deliveries={deliveries}
        onPaymentClick={(payment) => {
          setDocumentSource('list');
          setSelectedPayment(payment);
        }}
        onApprove={handlePaymentApprove}
        onPay={handlePaymentPay}
        onInvoiceClick={(invoiceNo) => {
          // 查找所有关联此发票的送货单
          const relatedDeliveries = deliveries.filter(d => d.invoice?.no === invoiceNo);
          if (relatedDeliveries.length > 0) {
            const delivery = relatedDeliveries[0];
            // 收集所有关联的结算单号
            const settlementNos = [...new Set(relatedDeliveries.map(d => d.settlement?.settlementNo).filter(Boolean))];

            setSelectedDocument({
              ...delivery,
              docType: 'invoice',
              docNo: invoiceNo,
              invoiceNo: invoiceNo,
              invoiceType: delivery.invoice?.type,
              amount: delivery.invoice?.amount,
              invoiceDate: delivery.invoice?.at,
              invoiceFile: delivery.invoice?.file,
              status: delivery.payment ? '已付款' : (delivery.invoice?.pendingApproval ? '审批中' : '已开票'),
              createdAt: delivery.invoice?.at,
              delivery,
              // 添加合并单据相关字段
              isMerged: settlementNos.length >= 2,
              settlementNos: settlementNos.length > 0 ? settlementNos : null,
              sourceSettlementNos: settlementNos.length > 0 ? settlementNos : null,
            });
          }
        }}
        onSettlementClick={(settlementNo) => {
          const delivery = deliveries.find(d => d.settlement?.settlementNo === settlementNo);
          if (delivery) {
            setSelectedDocument({
              ...delivery,
              docType: 'settlement',
              docNo: settlementNo,
              settlementNo: settlementNo,
              supplierName: delivery.supplierName,
              baseName: delivery.baseName,
              materialName: delivery.materialName,
              unitPrice: delivery.settlement?.unitPrice,
              actualWeight: delivery.settlement?.actualWeight,
              amount: delivery.settlement?.amount,
              status: delivery.settlement?.confirmedBySupplier ? '已确认' : (delivery.settlement?.approved ? '待确认' : '待审核'),
              createdAt: delivery.settlement?.at,
              stockInNos: delivery.settlement?.sourceStockInNos || (delivery.storage?.stockInNo ? [delivery.storage.stockInNo] : []),
              delivery,
            });
          }
        }}
        initialFilter={initialFilter}
        setInitialFilter={setInitialFilter}
      />;
      case 'users': return <UsersView users={users} onUpdateUser={handleUpdateUser} />;
      case 'settings': return <SettingsView />;
      case 'user_center': return <UserCenterView currentUser={currentUser} onLogout={handleLogout} />;
      case 'role_permission': return <RolePermissionView />;
      case 'operation_log': return <OperationLogView logs={mockOperationLogs} />;
      default: return <DashboardView />;
    }
  };

  const getPageTitle = () => {
      const titles = {
          dashboard: '工作台',
          approval: '审批管理',
          base: '基地管理',
          materials: '基地物料',
          sub_account: '子账号管理',
          demand: '采购需求',
          order: '订单管理',
          delivery: '送货单管理',
          arrival_notice: '预到货通知',
          stockin_manage: '入库单管理',
          settlement_manage: '结算单管理',
          invoice_manage: '发票单管理',
          document: '单据管理',
          reservation: '预约管理',
          stockin: '入库单管理',
          settlement: '结算管理',
          invoice: '发票管理',
          payment: '支付管理',
          users: '客商管理',
          settings: '规则配置',
          user_center: '个人中心',
          role_permission: '岗位权限',
          operation_log: '交易存证'
      };

      if (selectedDocument) return '单据详情';
      if (selectedOrder) return '订单详情';
      if (selectedDelivery) return '送货单详情';
      if (selectedPayment) return '付款单详情';

      return titles[activeTab] || '废钢直通车';
  };

  return (
    <>
    <div className="flex min-h-screen bg-slate-50 font-sans text-slate-900">
      <Sidebar
        activeTab={activeTab}
        setActiveTab={handleTabChange}
        currentRole={currentRole}
        onRoleChange={setCurrentRole}
        deliveryWaitingCount={deliveryWaitingCount}
      />
      <div className="flex-1 ml-64">
        <Header
          title={getPageTitle()}
          onEnterMobilePreview={() => setForceMobileView(true)}
          notifications={notifications}
          onMarkAsRead={markAsRead}
          onMarkAllAsRead={markAllAsRead}
          onNavigate={handleTabChange}
          todoCount={todoCount}
        />
        <main className="p-8">
          <div className="max-w-[1800px] mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500">
            {renderContent()}
          </div>
        </main>
      </div>
    </div>
    <ToastContainer toasts={toasts} onRemoveToast={removeToast} />
    </>
  );
}
